import React, { useState, useMemo } from "react";
import AppDataGrid from "../../common/AppDataGrid";
import {
  CircularProgress,
  Box,
  Container,
  Snackbar,
  Alert,
  Button,
} from "@mui/material";
import useFirestore from "../../../config/fireBaseHook";
import Add from "./inc/Add";
import Edit from "./inc/Edit";
import Delete from "./inc/Delete";
import EditUpload from "../../../hooks/EditUpload";
import ClearCollectionButton from "../../common/ClearCollectionButton";
import TechPrepPortalMgtBreadCrumbs from "../../Layout/TechPrepPortalMgtBreadCrumbs";
import { useAuth } from "../../../config/AuthContext";
import SimpleToolbar from "../../common/SimpleToolbar";
import CustomFilterPanel from "../../common/CustomFilterPanel";
import useCustomFilter from "../../../hooks/useCustomFilter";
import { courseShellFilters } from "../filterConfigurations";
import { normalizeIrn } from "../../../utils/irn";

const CourseShell = () => {
  const { userPermissions } = useAuth();
  const canEdit = userPermissions["Course Shell"]?.edit;
  const canDelete = userPermissions["Course Shell"]?.delete;
  const { data: courses, loading, error } = useFirestore("TpCourseShell");
  const { data: usersData } = useFirestore("users");

  // Fetch Sites data for the Sites dropdown
  const { data: sitesData } = useFirestore("Sites", "SiteName", "asc");
  // Fetch Partners so the grid/filters can resolve the live partner name
  // from tp_partner_irn instead of trusting the cached tp_partner string.
  const { data: partnersData } = useFirestore("TpPartners");
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [sortModel, setSortModel] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 100,
    page: 0,
  });
  const pageName = "Course Shell";

  // Renaming a site/partner in TpSites/TpPartners must not disconnect an
  // existing Course Shell record — sod_irn/tp_partner_irn are the stable
  // ids that survive a rename (see CourseShell/inc/Edit.js), so overlay
  // the *live* name onto every row instead of trusting the cached
  // tp_site_of_delivery/tp_partner string, which only ever gets refreshed
  // when that specific record is next opened in Edit. This feeds the
  // grid, the filters, and the "Export Data" button below, so all three
  // stay consistent with each other and with reality.
  const resolvedCourses = useMemo(() => {
    return (courses || []).map((course) => {
      const site = (sitesData || []).find(
        (s) => normalizeIrn(s.SiteID) === normalizeIrn(course.sod_irn),
      );
      const partner = (partnersData || []).find(
        (p) =>
          normalizeIrn(p.PartnerIRN) === normalizeIrn(course.tp_partner_irn),
      );
      return {
        ...course,
        tp_site_of_delivery: site?.SiteNameShort || course.tp_site_of_delivery,
        tp_partner: partner?.Tech_Prep_Partner || course.tp_partner,
      };
    });
  }, [courses, sitesData, partnersData]);

  // Create dynamic filter configuration with Sites and Partners options
  const dynamicFilters = useMemo(() => {
    const filters = [...courseShellFilters];

    // Update Sites filter with actual data
    const sitesFilterIndex = filters.findIndex(
      (f) => f.name === "tp_site_of_delivery",
    );
    if (sitesFilterIndex !== -1 && sitesData && sitesData.length > 0) {
      filters[sitesFilterIndex] = {
        ...filters[sitesFilterIndex],
        // tp_site_of_delivery is stored/displayed as SiteNameShort, not
        // SiteName — the options list must match what rows actually hold.
        options: sitesData
          .map((site) => ({
            value: site.SiteNameShort || site.SiteID || site.id,
            label: site.SiteNameShort || site.SiteID || site.id,
          }))
          .filter((option) => option.value) // Filter out empty values
          .sort((a, b) => a.label.localeCompare(b.label)), // Sort alphabetically
      };
    }

    // Update Partners filter with the distinct partners actually in use,
    // resolved live so a rename doesn't leave stale options/non-matches.
    const partnersFilterIndex = filters.findIndex(
      (f) => f.name === "tp_partner",
    );
    if (partnersFilterIndex !== -1 && resolvedCourses.length > 0) {
      const uniquePartners = [
        ...new Set(
          resolvedCourses
            .map((course) => course.tp_partner)
            .filter((partner) => partner && partner.trim() !== ""),
        ),
      ].sort();

      filters[partnersFilterIndex] = {
        ...filters[partnersFilterIndex],
        options: uniquePartners.map((partner) => ({
          value: partner,
          label: partner,
        })),
      };
    }

    return filters;
  }, [courseShellFilters, sitesData, resolvedCourses]);

  // Custom filter implementation
  const {
    filteredData,
    filterModel,
    setFilterModel,
    resetFilters,
    hasActiveFilters,
    isFiltering,
  } = useCustomFilter(resolvedCourses, dynamicFilters);

  // Handle filter changes
  const handleFiltersChange = (newFilters) => {
    setFilterModel(newFilters);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const getBooleanDisplayValue = (value) => {
    if (value === 0 || value === "0") return "No";
    if (value === -1 || value === "-1") return "Yes";
    if (value === null || typeof value === "undefined") return "";
    return String(value);
  };

  const columns = useMemo(
    () => [
      {
        field: "shell_name",
        headerName: "Shell Name",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "tp_final_approval",
        headerName: "TP Final Approval",
        flex: 1,
        minWidth: 150,
        renderCell: (params) => getBooleanDisplayValue(params.value),
      },
      {
        field: "tp_final_authorization_name",
        headerName: "TP Final Authorization Name",
        flex: 1,
        minWidth: 200,
      },
      {
        field: "tp_site_of_delivery",
        headerName: "TP Site of Delivery",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "tp_partner",
        headerName: "TP Partner",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "shell_school_year_start",
        headerName: "Shell School Year Start",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "shell_school_year_end",
        headerName: "Shell School Year End",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "shell_type",
        headerName: "Shell Type",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "tp_pathway_manager_name",
        headerName: "TP Pathway Manager",
        flex: 1,
        minWidth: 180,
        renderCell: (params) => {
          const managerId = params.row.tp_pathway_manager_ID;
          if (!managerId || !usersData) return "";
          const manager = usersData.find((user) => user.id === managerId);
          return manager ? manager.name : managerId;
        },
      },
      ...(canDelete
        ? [
            {
              field: "actions",
              headerName: "Actions",
              flex: 0.5,
              minWidth: 100,
              sortable: false,
              filterable: false,
              renderCell: (params) => (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent row click
                    setSelectedCourse(params.row);
                    setDeleteDialogOpen(true);
                  }}
                  sx={{ fontSize: "0.75rem", padding: "2px 8px" }}
                >
                  Delete
                </Button>
              ),
            },
          ]
        : []),
    ],
    [canDelete, usersData], // Add dependency for canDelete and usersData
  );

  // Prepare data for DataGrid
  const rows = useMemo(() => {
    if (!filteredData || !Array.isArray(filteredData)) return [];
    return filteredData.map((course, index) => ({
      id: course.id || index, // Use course.id if available, otherwise use index
      ...course,
    }));
  }, [filteredData]);

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedCourse(null);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setSelectedCourse(null);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleRecordUpdated = () => {
    setSnackbarMessage("Record has been updated successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
    setEditDialogOpen(false);
  };

  const handleRecordAdded = () => {
    setSnackbarMessage("Record has been added successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  };

  const handleRecordDeleted = () => {
    setSnackbarMessage("Record has been deleted successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
    setDeleteDialogOpen(false);
  };

  const handleBulkActionComplete = (success, message) => {
    setSnackbarMessage(
      message ||
        (success ? "Bulk operation successful." : "Bulk operation failed."),
    );
    setSnackbarSeverity(success ? "success" : "error");
    setSnackbarOpen(true);
  };

  const handleRowClick = (params) => {
    if (canEdit) {
      setSelectedCourse(params.row);
      setEditDialogOpen(true);
    }
  };

  const exportColumnOrder = columns.map((col) => col.field);

  const templateFields = Array.from(
    new Set([
      ...exportColumnOrder.filter((field) => field !== "actions"),
      "tp_term",
      "course",
      "subject",
      "credit_type",
      "tp_section_number",
      "primary_tp_inst_ID",
      "secondary_tp_inst_ID",
      "primary_fps_ID",
      "secondary_fps_ID",
      "tp_pathway_manager_ID",
      "tp_pathway_program_ID",
      "note_01",
      "tp_partner_irn",
      "sod_irn",
      "status",
      "what_year_taking_course",
      "tech_prep_modality",
      "credit_capture_scheme",
      "course_request_from",
      "credentials_uploaded",
      "credit_capture_status_note",
      "credit_capture_status",
    ]),
  );

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <p>Error fetching courses: {error.message}</p>
      </Box>
    );
  }

  return (
    <Container maxWidth={false} sx={{ height: "100vh", paddingTop: "50px" }}>
      <div style={{ height: "100%", width: "100%" }}>
        <TechPrepPortalMgtBreadCrumbs pageName={pageName} icon="faCog" />

        {/* Custom Filter Panel */}
        <CustomFilterPanel
          filterConfig={dynamicFilters}
          onFiltersChange={handleFiltersChange}
          initialFilters={filterModel}
          collapsible={true}
          title="Course Shell Filters"
          resultCount={filteredData?.length || 0}
          totalCount={courses?.length || 0}
          isSearching={isFiltering}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          {canEdit && <Add onRecordAdded={handleRecordAdded} />}
          {canEdit && (
            <EditUpload
              collectionName="TpCourseShell"
              onActionComplete={handleBulkActionComplete}
              exportColumnOrder={exportColumnOrder}
              exportData={resolvedCourses}
              templateFields={templateFields}
            />
          )}
          {canEdit && (
            <ClearCollectionButton collectionName="TpCourseShell" />
          )}
        </Box>
        <Box sx={{ height: "calc(100vh - 200px)", width: "100%" }}>
          <AppDataGrid
            rows={rows}
            columns={columns}
            slots={{
              toolbar: SimpleToolbar,
            }}
            slotProps={{
              toolbar: {
                fileName: "course-shell",
              },
            }}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[25, 50, 100, 200]}
            disableRowSelectionOnClick
            onRowClick={handleRowClick}
            sx={{
              "& .MuiDataGrid-row": {
                cursor: canEdit ? "pointer" : "default",
              },
            }}
          />
        </Box>
      </div>
      {selectedCourse && canEdit && (
        <Edit
          open={editDialogOpen}
          onClose={handleEditDialogClose}
          course={selectedCourse}
          onRecordUpdated={handleRecordUpdated}
        />
      )}
      {selectedCourse && canDelete && (
        <Delete
          open={deleteDialogOpen}
          onClose={handleDeleteDialogClose}
          course={selectedCourse}
          onRecordDeleted={handleRecordDeleted}
        />
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CourseShell;
